import { mapGetters, mapActions, mapMutations } from "vuex";
import { MAIN_APP_URL } from "@/common/Env";
import logEvent from "@/common/Analytics";
import Instrumentation from "@/common/instrumentation";
import { useProvideIsMutatedRecommendationFeedbacks } from "@/composables/recommendationFeedbacks/useProvideIsMutatedRecommendationFeedbacks";

import UsersApi from "@/common/http/UsersApi";

import LayoutDefaultFull from "@/layouts/LayoutDefaultFull/Component";
import LayoutSimple from "@/layouts/LayoutSimple/Component";

import AtsConnectModal from "@/components/AtsConnectModal/Component";
import CandidateReviewUndo from "@/components/CandidateReviewUndo";
import NewCampaignStatusNotification from "@/components/NewCampaignStatusNotification/NewCampaignStatusNotification";
import CandidateSidebar from "@/components/CandidateSidebar/Root/Component";
import ErrorLogo from "@/components/ErrorLogo";
import InviteTeammateModal from "@/components/InviteTeammateModal";
import CopyCampaignModal from "@/components/CopyCampaignModal/Component";
import NewCampaignMessageModal from "@/components/NewCampaignMessageModal";
import NewProjectModal from "@/components/NewProjectModal/NewProjectModal";
import SearchObserver from "@/components/SearchObserver";
import UserSendAsPermissionModal from "@/components/UserSendAsPermissionModal/Component";
import CreateCampaignModal from "@/components/CreateCampaignModal/CreateCampaignModal";

export default {
  name: "App",
  components: {
    AtsConnectModal,
    CandidateReviewUndo,
    CandidateSidebar,
    CreateCampaignModal,
    ErrorLogo,
    InviteTeammateModal,
    CopyCampaignModal,
    NewCampaignMessageModal,
    NewProjectModal,
    SearchObserver,
    UserSendAsPermissionModal,
    NewCampaignStatusNotification,
  },
  setup() {
    useProvideIsMutatedRecommendationFeedbacks();
  },
  props: {
    reportName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      layout: "div",
      loading: true,
    };
  },
  computed: {
    ...mapGetters([
      "authError",
      "currentCandidate",
      "currentTeam",
      "currentUser",
      "newCampaign",
      "generalError",
      "isAuthenticated",
      "isUserSudoing",
      "isChooseRoleNeeded",
      "lobbyPage",
      "showSendAsPermissionOptIn",
      "userJustLoggedIn",
      "vueBuildSha",
    ]),
    showLayout() {
      return this.isAuthenticated || this.isChooseRoleNeeded || this.lobbyPage || this.$route.meta.skipAuthRedirect;
    },
    sendAsPermissionModalVisible() {
      return this.$route.name === "user-send-as-permission-modal";
    },
    atsConnectModalVisible() {
      return this.$route.name === "ats-connect-modal";
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.query.notice) {
          this.$message({
            showClose: true,
            message: to.query.notice,
            type: "info",
          });
          const query = Object.assign({}, to.query);
          delete query.notice;
          this.$router.replace({ query });
        }

        this.setLayout();

        if (to.meta?.title && !this.currentCandidate?.tfId ) {
          this.setDocumentTitle(to.meta.title);
        }

        this.showAtsConnectModal();
      },
    },
    "currentUser.id"() {
      if (this.isAuthenticated) {
        if (this.currentUser.role !== "recruiter_accord") {
          this.showSendAsPermissionModal();
          this.fetchReviewCount();
          // 10 minutes
          setInterval(this.fetchReviewCount, 600000);
        }

        localStorage.setItem("vueBuildSha", this.vueBuildSha);

        this.requestHeavyData();

        const twelveHours = 12 * 60 * 60 * 1000; /* ms */

        if (!this.currentUser.appVisitedAt || (Date.now() - new Date(this.currentUser.appVisitedAt).getTime()) > twelveHours) {
          UsersApi.update({ appVisitedAt: new Date() });
        }

        // Load the user for the beacon support/help if there is a user
        window.Beacon("identify", {
          name: `${this.currentUser.name}`,
          email: `${this.currentUser.email}`,
          company: `${this.currentUser.primaryTeamName || ""}`,
        });
      }
    },
    lobbyPage: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$router.replace({ name: val });
        }
      },
    },
    isChooseRoleNeeded: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$router.replace({ name: "choose-role" });
        }
      },
    },
    authError: {
      immediate: true,
      handler(error) {
        if (error && error.status !== 500) {
          // root URL is a hub that picks the right URL for the user
          window.location.href = MAIN_APP_URL;
        }
      },
    },
    userJustLoggedIn(justLoggedIn) {
      if (justLoggedIn && !this.isUserSudoing) {
        if (justLoggedIn === "initial") {
          this.updateUserTimezone();
        }

        this.$router.replace({ name: "dashboard" });

        setTimeout(() => {
          this.$message({
            showClose: true,
            message: `Welcome, ${this.currentUser.name}! You've successfully signed in.`,
            type: "success",
            duration: 3000,
          });
        }, 2000);
      }
    },
    generalError(error) {
      if (!error) {
        return;
      }

      const errorMessage = error.message || error.toString();
      if (error.name === "SemanticError") {
        const message = `Oops. ${errorMessage}.`;
        this.$message({
          showClose: true,
          message,
          type: "info",
          duration: 10000,
        });
      }

      logEvent("error", { message: errorMessage });
    },
  },
  methods: {
    ...mapActions([
      "fetchReviewCount",
      "fetchReviewData",
    ]),
    ...mapMutations([
      "setCurrentUserTimezone",
      "setDocumentTitle",
      "setWindowInnerWidth",
    ]),
    setLayout() {
      this.setWindowInnerWidth(window.innerWidth);

      if (this.$route.meta.layout === false || this.$route.name === null) {
        this.layout = "div";
      // Checking for authentication prevents search box from appearing and then
      // disappearing for the lobby pages.
      } else if (this.$route.meta.layout === "simple" || !this.isAuthenticated) {
        this.layout = LayoutSimple;
      } else {
        this.layout = LayoutDefaultFull;
      }
    },
    updateUserTimezone() {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.setCurrentUserTimezone(timezone);
      UsersApi.update({ timezone });
    },
    showSendAsPermissionModal() {
      if (this.showSendAsPermissionOptIn && this.$route.name !== "user-send-as-permission-modal") {
        this.$router.replace({ name: "user-send-as-permission-modal" });
      }
    },
    showAtsConnectModal() {
      if (this.currentUser.showAtsConnect && this.$route.name !== "ats-connect-modal") {
        this.$router.replace({ name: "ats-connect-modal" });
      }
    },
    onPageScrollHandler() {
      const content = document.getElementById("tf-content");

      if (content) {
        const elementHeight = content.scrollHeight;
        const visibleHeight = content.clientHeight;
        const scrollPosition = content.scrollTop;
        const maxTop = elementHeight - visibleHeight - scrollPosition;

        if (visibleHeight + 500 >= maxTop) {
          this.$bus.$emit("page-scroll-end");
        }
      }
    },
    async requestHeavyData() {
      const span = Instrumentation.startSpan("app:heavyData");
      await this.fetchReviewData();
      Instrumentation.endSpan(span);
    },
  },
  created() {
    const checkElement = () => {
      const content = document.getElementById("tf-content");
      if (content) {
        content.addEventListener("scroll", this.onPageScrollHandler);
      } else {
        // Retry after a short delay if the element isn't found
        setTimeout(checkElement, 50);
      }
    };

    // Call this function to check if there is content, if the DOM hasn't
    // loaded the content the function will call itself recursively every 50ms
    // until the content has been loaded
    checkElement();
  },
  beforeDestroy() {
    const content = document.getElementById("tf-content");
    if (content) {
      content.removeEventListener("scroll", this.onPageScrollHandler);
    }
  },
};
